import { ChevronDown } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'
import { useActiveSection } from '@/landing/contexts/ActiveSectionContext'
import type { MobileSectionProps } from '@/types/components'

export const MobileSection: React.FC<MobileSectionProps> = ({
  title,
  subtitle,
  items,
  onItemClick,
  showIcons = true,
  t,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const { setActiveSection } = useActiveSection()

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full p-3 text-left rounded-lg hover:bg-accent transition-colors"
      >
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-muted-foreground transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div className="mt-2 ml-4 space-y-2 border-l border-border pl-4">
          {items.map((item) => (
            <a
              key={item.title}
              href={item.comingSoon ? undefined : item.href}
              target={item.isExternal ? '_blank' : undefined}
              rel={item.isExternal ? 'noopener noreferrer' : undefined}
              onClick={(e) => {
                // Allow external links to open normally
                if (item.isExternal) {
                  onItemClick()
                  return
                }

                // Block navigation for inactive sections
                if (item.comingSoon) {
                  e.preventDefault()
                  return
                }

                if (item.id) {
                  // If we're already on the black book page, prevent navigation and just scroll
                  if (window.location.pathname === '/blackbook') {
                    e.preventDefault()
                    setActiveSection(item.id)
                    // Scroll to the appropriate section
                    const targetId = item.id === 'how-to-use' ? 'how-to-use' : 'black-book-sections'
                    const element = document.getElementById(targetId)
                    if (element) {
                      const elementPosition =
                        element.getBoundingClientRect().top + window.pageYOffset
                      const offsetPosition = elementPosition - 80 // 64px header height + 16px padding

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                      })
                    }
                  } else {
                    // Let the href navigate to /black book#section-id
                    setActiveSection(item.id)
                  }
                }
                onItemClick()
              }}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                item.comingSoon
                  ? 'opacity-50 cursor-not-allowed bg-accent/50'
                  : 'hover:bg-accent cursor-pointer'
              }`}
            >
              {showIcons && <item.icon className="h-5 w-5 text-muted-foreground mt-0.5" />}
              <div>
                <div className="font-medium text-sm flex items-center gap-2">
                  {item.title}
                  {item.comingSoon && (
                    <span className="inline-flex items-center px-2 py-1 text-[10px] font-bold text-white bg-primary rounded-full">
                      {t('navigation.coming_soon') || 'COMING SOON'}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {item.description}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
